import { OTLayout } from "@/components/layout/ot-layout";
import { Users, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { useState } from "react";
import { Link } from "wouter";

const INVOLVEMENT_ROUTES = [
  {
    title: "RCOT SS Rheumatology Committee Membership",
    description: "Join the SS Rheumatology committee as an elected or co-opted member to influence national OT priorities, events, and resources in rheumatology.",
    detail: "Committee roles are advertised via RCOT and the SS Rheumatology newsletter. Most roles require current RCOT SS Rheumatology membership and relevant clinical experience. Committee members contribute to position statements, CPD events, consultation responses, and national strategy.",
  },
  {
    title: "BSR OT Special Interest Group (SIG)",
    description: "The British Society for Rheumatology OT SIG brings together rheumatology OTs within BSR's MDT framework.",
    detail: "The BSR OT SIG contributes to BSR clinical guidelines, annual conference content, and MDT working groups. Membership of BSR (as an allied health professional) and the OT SIG gives access to BSR resources, guideline development consultations, and national networking.",
  },
  {
    title: "Specialist Competency Recognition",
    description: "The RCOT SS Rheumatology specialist competency framework provides a structure for recognising and demonstrating specialist OT expertise in rheumatology.",
    detail: "OTs working in rheumatology for several years and engaging with RCOT SS Rheumatology CPD can work towards specialist recognition. This involves mapping practice against the specialist competency areas, compiling evidence, and documenting practice against RCOT standards. Specialist competency evidence strengthens advanced practice and Consultant OT applications.",
  },
  {
    title: "Contributing to National Guideline Development",
    description: "Rheumatology OTs can contribute to NICE, BSR, and RCOT guideline development through formal consultation processes and working group participation.",
    detail: "NICE and BSR routinely consult on new and updated musculoskeletal guidelines. OTs can respond to consultations as individuals or through RCOT. For greater influence, apply to join NICE guideline development groups as a patient or professional representative. RCOT SS Rheumatology may coordinate joint OT consultation responses.",
  },
  {
    title: "RCOT SS Rheumatology CPD Events",
    description: "Presenting at RCOT SS Rheumatology study days, webinars, or OTnews is a recognised way to contribute to the profession and build national visibility.",
    detail: "Abstract submissions for RCOT Annual Conference and SS Rheumatology study days are open annually. Presenting case studies, service evaluations, or research significantly raises your national profile. Regular presenters are often invited to join planning committees or take on editorial roles in OTnews.",
  },
];

const TIMELINE = [
  { phase: "Early career (Band 5–6)", actions: ["Join RCOT and RCOT SS Rheumatology", "Attend SS Rheumatology study days", "Respond to NICE/RCOT consultations as an individual"] },
  { phase: "Mid career (Band 7)", actions: ["Present at RCOT SS Rheumatology events or RCOT Annual Conference", "Apply to join BSR OT SIG", "Contribute to national consultation responses via RCOT"] },
  { phase: "Senior / Consultant (Band 8+)", actions: ["Apply for RCOT SS Rheumatology committee roles", "Join NICE guideline development groups", "Contribute to RCOT or BSR position statement development", "Build toward FRCOT with national contribution evidence"] },
];

export default function OtSsRheumatologyCareer() {
  const [openRoute, setOpenRoute] = useState<number | null>(null);
  const [openPhase, setOpenPhase] = useState<number | null>(null);

  return (
    <OTLayout title="SS Rheumatology Specialist Career">
      <div className="space-y-6">

        <div className="bg-gradient-to-br from-blue-700 to-blue-900 rounded-2xl p-6 text-white">
          <div className="flex items-center gap-3 mb-2">
            <Users className="h-7 w-7 text-blue-300" />
            <div>
              <h2 className="text-xl font-bold">RCOT SS Rheumatology Specialist Career</h2>
              <p className="text-blue-200 text-sm">Building a national specialist OT career through RCOT SS Rheumatology</p>
            </div>
          </div>
          <p className="text-blue-100 text-sm leading-relaxed">
            Contributing to RCOT SS Rheumatology, BSR OT SIG, and national guideline development is a defining feature of a specialist rheumatology OT career. This page outlines how to get involved, build national influence, and use specialist section engagement to support progression to Consultant OT and RCOT Fellowship.
          </p>
          <a
            href="https://www.rcot.co.uk/about-occupational-therapy/specialist-sections/rheumatology"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 mt-4 text-sm text-blue-200 hover:text-white font-medium"
          >
            <ExternalLink className="h-4 w-4" />
            RCOT SS Rheumatology website
          </a>
        </div>

        <div className="space-y-3">
          {INVOLVEMENT_ROUTES.map((r, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
              <button
                onClick={() => setOpenRoute(openRoute === i ? null : i)}
                className="flex items-center justify-between w-full px-6 py-4 text-left hover:bg-slate-50 transition-colors"
              >
                <div>
                  <p className="text-sm font-semibold text-slate-800">{r.title}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{r.description}</p>
                </div>
                {openRoute === i ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0 ml-4" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0 ml-4" />}
              </button>
              {openRoute === i && (
                <div className="px-6 pb-5 border-t border-slate-100 pt-3">
                  <p className="text-sm text-slate-600 leading-relaxed">{r.detail}</p>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-4">Getting Involved at Each Career Stage</h3>
          <div className="space-y-3">
            {TIMELINE.map((t, i) => (
              <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenPhase(openPhase === i ? null : i)}
                  className="flex items-center justify-between w-full px-4 py-3 text-left hover:bg-slate-50 transition-colors"
                >
                  <span className="text-sm font-semibold text-slate-800">{t.phase}</span>
                  {openPhase === i ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
                </button>
                {openPhase === i && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-3">
                    <ul className="space-y-1.5">
                      {t.actions.map((a, j) => (
                        <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                          <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <h3 className="font-bold text-slate-900 mb-3">Log your specialist section CPD</h3>
          <p className="text-sm text-slate-500 mb-4">Track your RCOT SS Rheumatology engagement, committee work, and national contributions in your CPD activity log and Advanced Practice Portfolio.</p>
          <div className="flex flex-wrap gap-3">
            <Link href="/ot/cpd">
              <button className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-xl text-sm font-medium transition-colors">
                CPD Activity Log
              </button>
            </Link>
            <Link href="/ot/cpd/advanced-practice">
              <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors">
                Advanced Practice Portfolio
              </button>
            </Link>
            <Link href="/ot/cpd/specialist-section">
              <button className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-sm font-medium transition-colors">
                SS Rheumatology Hub
              </button>
            </Link>
          </div>
        </div>

        <div className="text-xs text-slate-400 bg-slate-50 rounded-xl px-4 py-3 leading-relaxed">
          <strong>RCOT SS Rheumatology:</strong> Information based on publicly available RCOT and BSR resources. Committee structures, election processes, and involvement opportunities change regularly. Check the RCOT SS Rheumatology website and OTnews for current opportunities.
        </div>
      </div>
    </OTLayout>
  );
}
